import React, { useMemo, useEffect } from "react";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useMeterReadingFormatDetail,
  useMeterReadingStatusList,
  useMeterReasonCodesList,
} from "@features/mx/settings/hooks";
import { useUploadReading } from "@features/mx/device-management/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface AddReadDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  row: any;
  remoteUtilityId: string;
  formatId?: string | number;
  refetch?: () => void;
}

export function AddReadDialog({
  open,
  setOpen,
  row,
  remoteUtilityId,
  formatId,
  refetch,
}: AddReadDialogProps) {
  const { toast } = useToast();

  // Fetch schema dynamically if formatId is provided
  const { data: formatDetail } = useMeterReadingFormatDetail(
    formatId ? String(formatId) : "",
    getRemoteUtilityId(),
  );

  // Fetch dropdown options for status and reason codes
  const { data: statusList } = useMeterReadingStatusList(remoteUtilityId, 1, 100);
  const { data: reasonCodes } = useMeterReasonCodesList(remoteUtilityId, 1, 100);

  const { mutateAsync: uploadReading, isPending: isSaving } = useUploadReading(row?.meterId);

  const conditionStatusOptions = useMemo(() => {
    const raw = statusList?.results || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label: item.name,
        value: String(item.id),
      }));
    }
    return [
     
    ];
  }, [statusList]);

  const conditionReasonOptions = useMemo(() => {
    const raw = reasonCodes?.results || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label:item.reason,
        value: String(item.id),
      }));
    }
    return [
     
    ];
  }, [reasonCodes]);

  // Transform layout sections array to FormField list
  const formFields = useMemo(() => {
    const layout = formatDetail;
    const flatFields: FormField[] = [];

    if (layout?.sections && Array.isArray(layout.sections)) {
      layout.sections.forEach((sec: any) => {
        if (sec.fields && Array.isArray(sec.fields)) {
          sec.fields.forEach((f: any) => {
            if (f.isActive === false) return;

            let type: FormField["type"] = "text";
            if (f.fieldType === "DATE") {
              type = "datepicker";
            } else if (f.fieldType === "NUMERIC") {
              type = "number";
            } else if (f.fieldType === "DROPDOWN") {
              type = "select";
            } else if (f.fieldType === "PHOTO") {
              type = "file";
            }

            const field: FormField = {
              name: f.key,
              label: f.fieldName,
              type,
              required: f.required,
              placeholder: `Enter ${f.fieldName.toLowerCase()}`,
              className: "col-span-1",
            };

            if (f.fieldType === "DROPDOWN") {
              if (f.key === "condition_status") {
                field.options = conditionStatusOptions;
              } else if (f.key === "condition_reason") {
                field.options = conditionReasonOptions;
              } else {
                field.options = [];
              }
            }

            flatFields.push(field);
          });
        }
      });
    }

    // Sort by sequenceId
    const allRawFields = layout?.sections?.flatMap((s: any) => s.fields || []) || [];
    flatFields.sort((a, b) => {
      const fieldA = allRawFields.find((f: any) => f.key === a.name);
      const fieldB = allRawFields.find((f: any) => f.key === b.name);
      return (fieldA?.sequenceId || 0) - (fieldB?.sequenceId || 0);
    });

    return flatFields;
  }, [formatDetail, conditionStatusOptions, conditionReasonOptions]);

  // Create form in component body (adhering strictly to Hook rules)
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  // Reset form when dialog opens or fields change
  useEffect(() => {
    if (formFields.length > 0 && open) {
      const defaultValues: Record<string, any> = {};
      formFields.forEach((f) => {
        defaultValues[f.name] = f.type.toLowerCase() === "date" ? new Date().toISOString().substring(0, 10) : "";
      });
      form.reset(defaultValues);
    }
  }, [formFields, open, form]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: any) => {
    if (!row) return;

    try {
      const formData = new FormData();
      formData.append("remote_utility_id", String(getRemoteUtilityId()));
      if (data.previousReading !== undefined) formData.append("previous_reading", data.previousReading);
      if (data.previousReadingDate) formData.append("previous_reading_date", data.previousReadingDate);
      if (data.currentReading !== undefined) formData.append("current_reading", data.currentReading);
      if (data.currentReadingDate) formData.append("current_reading_date", data.currentReadingDate);
      if (data.readingStatus) formData.append("reading_status", data.readingStatus);
      if (data.remark) formData.append("remark", data.remark);
      if (data.photoUpload?.length > 0) formData.append("file", data.photoUpload[0]);

      // Include any additional dynamic fields from the format
      Object.entries(data).forEach(([key, value]) => {
        if (!['previousReading','previousReadingDate','currentReading','currentReadingDate','readingStatus','remark','photoUpload'].includes(key) && value !== undefined && value !== '') {
          formData.append(key, String(value));
        }
      });

      await uploadReading(formData);

      toast({
        title: "Success",
        description: "Meter reading added successfully.",
        variant: "default",
      });

      refetch?.();
      handleClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add meter reading. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCustomSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Meter Reading</DialogTitle>
          <DialogDescription>
            Enter the reading details for Job Card: <span className="font-semibold text-gray-900">{row?.jobCard}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {formFields.length > 0 ? (
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={onSubmit}
              gridColumns={1}
              config={{
                showSubmitButton: false,
                classes: FIELD_CLASSES,
              }}
            />
          ) : (
            <p className="text-sm text-gray-500">Loading form configuration...</p>
          )}
        </div>

        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCustomSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Reading"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
export default AddReadDialog;
